// Unified image export: naming rules + ZIP assembly for both export flows
// (ProjectDetailsView download modal and ProjectWorkspaceView export tab).

import JSZip from 'jszip';
import type { GalleryImage } from '@/types';
import { getRoomLabel } from '@/lib/roomConfig';
import { DEFAULT_AI_LABEL_OPACITY, DEFAULT_AI_LABEL_POSITION, type AiLabelPosition } from '@/lib/aiLabelPrefs';

export type ExportVariant =
  | 'eingerichtet'
  | 'leer'
  | 'original'
  | 'eingerichtet_gebrandet'
  | 'leer_gebrandet'
  | 'titelbild';

export interface ExportEntry {
  imageId: string;
  roomLabel: string;
  variant: ExportVariant;
  source: string | Blob;
  ext?: 'png' | 'jpg';
}

export interface FloorplanEntry {
  side: '2d' | '3d';
  source: string | Blob;
  ext?: 'png' | 'jpg';
}

export interface PlannedFile {
  path: string;
  source: string | Blob;
}

const UMLAUT_MAP: Record<string, string> = {
  ä: 'ae', ö: 'oe', ü: 'ue', Ä: 'Ae', Ö: 'Oe', Ü: 'Ue', ß: 'ss',
};

export const sanitizeExportName = (name: string): string =>
  name
    .replace(/[äöüÄÖÜß]/g, (c) => UMLAUT_MAP[c])
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_.-]/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

export const exportZipFileName = (projectName: string): string =>
  `${sanitizeExportName(projectName) || 'estage'}_Bilder.zip`;

export const planExportFiles = (
  entries: ExportEntry[],
  floorplans: FloorplanEntry[]
): PlannedFile[] => {
  const files: PlannedFile[] = [];
  const numberByImage = new Map<string, string>(); // "{label}:{imageId}" → "01"
  const countByLabel = new Map<string, number>();
  let heroCount = 0;

  for (const e of entries) {
    if (e.variant === 'titelbild') {
      heroCount++;
      const name = heroCount === 1 ? 'Titelbild' : `Titelbild_${String(heroCount).padStart(2, '0')}`;
      files.push({ path: `Immobilienbilder/${name}.${e.ext ?? 'png'}`, source: e.source });
      continue;
    }
    const label = sanitizeExportName(e.roomLabel) || 'Bild';
    const key = `${label}:${e.imageId}`;
    let nn = numberByImage.get(key);
    if (!nn) {
      const next = (countByLabel.get(label) ?? 0) + 1;
      countByLabel.set(label, next);
      nn = String(next).padStart(2, '0');
      numberByImage.set(key, nn);
    }
    files.push({
      path: `Immobilienbilder/${label}_${nn}_${e.variant}.${e.ext ?? 'png'}`,
      source: e.source,
    });
  }

  for (const fp of floorplans) {
    files.push({
      path: `Grundriss/Grundriss_${fp.side.toUpperCase()}.${fp.ext ?? 'png'}`,
      source: fp.source,
    });
  }
  return files;
};

export interface DownloadVersionSelection {
  branded: boolean;
  staged: boolean;
  empty: boolean;
  original: boolean;
  hero: boolean;
  floorplan: boolean;
}

export const galleryEntriesForVersions = (
  images: GalleryImage[],
  versions: DownloadVersionSelection
): ExportEntry[] => {
  const entries: ExportEntry[] = [];
  // Aufsteigend nach created_at, damit das älteste Bild _01 erhält (stabile Nummern).
  const sorted = [...images].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const img of sorted) {
    const roomLabel = img.customLabel || getRoomLabel(img.roomType);
    if (versions.branded) {
      if (img.brandedUrl) entries.push({ imageId: img.id, roomLabel, variant: 'eingerichtet_gebrandet', source: img.brandedUrl });
      if (img.brandedEmptyUrl) entries.push({ imageId: img.id, roomLabel, variant: 'leer_gebrandet', source: img.brandedEmptyUrl });
    }
    if (versions.staged && img.stagedUrl) entries.push({ imageId: img.id, roomLabel, variant: 'eingerichtet', source: img.stagedUrl });
    if (versions.empty && img.emptyUrl) entries.push({ imageId: img.id, roomLabel, variant: 'leer', source: img.emptyUrl });
    if (versions.original && img.originalUrl) entries.push({ imageId: img.id, roomLabel, variant: 'original', source: img.originalUrl });
  }
  if (versions.hero) {
    const heroImg = images.find((img) => img.heroUrl);
    if (heroImg?.heroUrl) {
      entries.push({ imageId: heroImg.id, roomLabel: 'Titelbild', variant: 'titelbild', source: heroImg.heroUrl });
    }
  }
  return entries;
};

/**
 * Rendert den KI-Hinweis in eine Quelle und liefert das Ergebnis als Blob.
 * Injizierbar, damit die Auswahl-Logik ohne Canvas testbar bleibt.
 */
export type AiLabelRenderer = (
  source: string | Blob,
  position: AiLabelPosition,
  opacity: number
) => Promise<Blob>;

/**
 * Default-Renderer per dynamischem Import: imageProcessing zieht Canvas + den
 * Supabase-Client in den Modulgraph — der Export-Kern (und seine Tests) bleibt
 * so frei davon.
 */
const defaultAiLabelRenderer: AiLabelRenderer = async (source, position, opacity) => {
  const { applyAiLabelToImage } = await import('@/services/imageProcessing');
  return applyAiLabelToImage(source, position, opacity);
};

/**
 * Pre-Step vor planExportFiles/ZIP-Assembly: brennt "Mit KI erstellt" in alle
 * KI-Ergebnisse ein — jede Bild-Variante außer 'original' sowie beide
 * Grundriss-Seiten. Originale bleiben byte-identisch (unveränderte Quelle),
 * denn sie sind das Foto des Maklers, nicht unser Ergebnis.
 *
 * Aufruf NACH dem Branding: die entries tragen zu diesem Zeitpunkt bereits die
 * gebrandeten Quellen, der Hinweis liegt also obenauf.
 */
export const applyAiLabel = async (
  entries: ExportEntry[],
  floorplans: FloorplanEntry[],
  position: AiLabelPosition = DEFAULT_AI_LABEL_POSITION,
  opacity: number = DEFAULT_AI_LABEL_OPACITY,
  render: AiLabelRenderer = defaultAiLabelRenderer
): Promise<{ entries: ExportEntry[]; floorplans: FloorplanEntry[] }> => {
  const labeledEntries: ExportEntry[] = [];
  for (const e of entries) {
    if (e.variant === 'original') {
      labeledEntries.push(e);
      continue;
    }
    labeledEntries.push({ ...e, source: await render(e.source, position, opacity) });
  }

  const labeledFloorplans: FloorplanEntry[] = [];
  for (const fp of floorplans) {
    labeledFloorplans.push({ ...fp, source: await render(fp.source, position, opacity) });
  }

  return { entries: labeledEntries, floorplans: labeledFloorplans };
};

export interface ExportZipResult {
  blob: Blob;
  added: number;
  failed: number;
  total: number;
}

export interface AssembleZipOptions {
  onProgress?: (percent: number) => void;
  /**
   * Optionale Pro-Datei-Transformation (z. B. Branding einbrennen, komprimieren).
   * Ein Fehler hier bricht den gesamten Export ab (fail-loud) — reine
   * Ladefehler einer Quelle überspringen dagegen nur die einzelne Datei.
   */
  transform?: (source: string | Blob, file: PlannedFile) => Promise<string | Blob>;
}

const sourceToData = async (source: string | Blob): Promise<ArrayBuffer | Blob> => {
  if (typeof source !== 'string') return source;
  const res = await fetch(source);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.arrayBuffer();
};

export const assembleExportZip = async (
  planned: PlannedFile[],
  opts: AssembleZipOptions = {}
): Promise<ExportZipResult> => {
  const zip = new JSZip();
  let added = 0;
  let failed = 0;
  let processed = 0;

  for (const file of planned) {
    const source = opts.transform ? await opts.transform(file.source, file) : file.source;
    try {
      zip.file(file.path, await sourceToData(source));
      added++;
    } catch (err) {
      console.warn(`[Export] Datei übersprungen: ${file.path}`, err);
      failed++;
    }
    processed++;
    opts.onProgress?.(Math.round((processed / planned.length) * 100));
  }

  if (added === 0) {
    throw new Error('Export fehlgeschlagen: keine Datei konnte geladen werden.');
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  return { blob, added, failed, total: planned.length };
};

export const buildExportZip = async (
  entries: ExportEntry[],
  floorplans: FloorplanEntry[],
  onProgress?: (percent: number) => void
): Promise<ExportZipResult> =>
  assembleExportZip(planExportFiles(entries, floorplans), { onProgress });
